import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import { useTranslation } from 'react-i18next';

interface MannequinPhotoUploadProps {
    initialPhotoUrl?: string;
    onFileSelect: (file: File | null) => void;
}

type PreviewStatus = 'idle' | 'loading' | 'loaded' | 'error';

const isLocalPreviewUrl = (url: string) => url.startsWith('data:') || url.startsWith('blob:');

export const MannequinPhotoUpload: React.FC<MannequinPhotoUploadProps> = ({ initialPhotoUrl, onFileSelect }) => {
    const { t } = useTranslation();
    const [previewUrl, setPreviewUrl] = useState<string | null>(initialPhotoUrl || null);
    const [previewStatus, setPreviewStatus] = useState<PreviewStatus>(initialPhotoUrl ? 'loading' : 'idle');
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setPreviewUrl(initialPhotoUrl || null);
    }, [initialPhotoUrl]);

    useEffect(() => {
        if (!previewUrl) {
            setPreviewStatus('idle');
            return;
        }

        if (isLocalPreviewUrl(previewUrl)) {
            setPreviewStatus('loaded');
            return;
        }

        let isCancelled = false;
        setPreviewStatus('loading');

        const image = new Image();
        image.onload = () => {
            if (!isCancelled) {
                setPreviewStatus('loaded');
            }
        };
        image.onerror = () => {
            if (!isCancelled) {
                setPreviewStatus('error');
            }
        };
        image.src = previewUrl;

        return () => {
            isCancelled = true;
            image.onload = null;
            image.onerror = null;
        };
    }, [previewUrl]);

    const handleFileSelect = (file: File) => {
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
            onFileSelect(file);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleRemovePhoto = (e: React.MouseEvent) => {
        e.stopPropagation();
        setPreviewUrl(null);
        onFileSelect(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: 300,
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                bgcolor: isDragging ? 'action.hover' : 'background.paper',
                transition: 'all 0.2s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                },
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleClick}
        >
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                        handleFileSelect(e.target.files[0]);
                    }
                }}
            />

            {previewUrl ? (
                <>
                    {previewStatus === 'error' ? (
                        <Box sx={{ textAlign: 'center', p: 3 }}>
                            <ImageNotSupportedIcon sx={{ fontSize: '48px', marginBottom: '16px', color: '#9e9e9e' }} />
                            <Typography variant="body2" color="textSecondary">
                                {t('mannequin.photoLoadError')}
                            </Typography>
                        </Box>
                    ) : previewStatus === 'loaded' ? (
                        <img
                            src={previewUrl}
                            alt={t('mannequin.photo')}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                            }}
                            onError={() => setPreviewStatus('error')}
                        />
                    ) : (
                        <CircularProgress size={32} />
                    )}
                    <IconButton
                        onClick={handleRemovePhoto}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.7)',
                            },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: '20px' }} />
                    </IconButton>
                </>
            ) : (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                    <CloudUploadIcon sx={{ fontSize: '48px', marginBottom: '16px', color: '#9e9e9e' }} />
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                        {t('mannequin.dropPhotoHere')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t('mannequin.orClickToSelect')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
